if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('.product-form__submit');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.wishlistButton = this.querySelector('.zuke-product-wishlist-button');
        this.submitButton.addEventListener('click', this.onNotifyMeClick.bind(this));
        this.wishlistButton?.addEventListener('click', this.onProductWishlistClick.bind(this));
        this.syncProductWishlistButton();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onNotifyMeClick(evt) {
        if (!this.submitButton.hasAttribute('data-notify-me-button')) return;
        evt.preventDefault();
        this.showNotifyMeForm();
      }

      onProductWishlistClick(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();

        const item = {
          url: this.wishlistButton.dataset.url || window.location.pathname,
          title: this.wishlistButton.dataset.title || document.querySelector('.product__title h1, .product__title')?.textContent?.trim() || 'Product',
          image: this.wishlistButton.dataset.image || '',
          price: this.wishlistButton.dataset.price || '',
        };
        const items = this.readProductWishlist();
        const existing = items.find((wishlistItem) => this.cleanProductWishlistUrl(wishlistItem.url) === this.cleanProductWishlistUrl(item.url));
        const nextItems = existing
          ? items.filter((wishlistItem) => this.cleanProductWishlistUrl(wishlistItem.url) !== this.cleanProductWishlistUrl(item.url))
          : [...items, item];

        localStorage.setItem('zuke:wishlist', JSON.stringify(nextItems));
        this.syncProductWishlistButton(nextItems);
        this.syncWishlistHeader(nextItems);
      }

      readProductWishlist() {
        try {
          return JSON.parse(localStorage.getItem('zuke:wishlist') || '[]');
        } catch (error) {
          return [];
        }
      }

      cleanProductWishlistUrl(url) {
        const anchor = document.createElement('a');
        anchor.href = url || window.location.pathname;
        return anchor.pathname.replace(/\/$/, '');
      }

      syncProductWishlistButton(items = this.readProductWishlist()) {
        if (!this.wishlistButton) return;

        const active = items.some(
          (item) => this.cleanProductWishlistUrl(item.url) === this.cleanProductWishlistUrl(this.wishlistButton.dataset.url)
        );
        this.wishlistButton.classList.toggle('is-active', active);
        this.wishlistButton.setAttribute('aria-pressed', String(active));
        this.wishlistButton.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
        this.wishlistButton.querySelector('[data-wishlist-label]').textContent = active ? 'Wishlisted' : 'Wishlist';
      }

      syncWishlistHeader(items) {
        const header = document.querySelector('.header__icon--wishlist');
        if (!header) return;

        let count = header.querySelector('.zuke-wishlist-count');
        if (!count) {
          count = document.createElement('span');
          count.className = 'zuke-wishlist-count';
          header.appendChild(count);
        }
        count.textContent = items.length;
        count.dataset.count = String(items.length);
        count.hidden = items.length === 0;
        count.setAttribute('aria-hidden', items.length === 0 ? 'true' : 'false');
      }

      showNotifyMeForm() {
        const existingPanel = this.querySelector('.zuke-notify-me-panel');
        if (existingPanel) {
          existingPanel.hidden = !existingPanel.hidden;
          existingPanel.querySelector('input[type="email"]')?.focus();
          return;
        }

        const panel = document.createElement('form');
        panel.className = 'zuke-notify-me-panel';
        panel.method = 'post';
        panel.action = '/contact';
        panel.innerHTML = `
          <input type="hidden" name="form_type" value="contact">
          <input type="hidden" name="contact[tags]" value="notify-me">
          <input type="hidden" name="contact[body]" value="">
          <div class="field">
            <input class="field__input" type="email" name="contact[email]" autocomplete="email" placeholder="Email" required>
            <label class="field__label">Email</label>
          </div>
          <button type="submit" class="button button--primary button--full-width">Submit</button>
          <p class="form__message zuke-notify-me-message" hidden></p>
        `;

        panel.addEventListener('submit', this.onNotifyMeSubmit.bind(this));
        this.form.insertAdjacentElement('afterend', panel);
        panel.querySelector('input[type="email"]')?.focus();
      }

      onNotifyMeSubmit(evt) {
        evt.preventDefault();

        const notifyForm = evt.currentTarget;
        const emailInput = notifyForm.querySelector('input[type="email"]');
        const message = notifyForm.querySelector('.zuke-notify-me-message');
        if (!emailInput.checkValidity()) {
          emailInput.reportValidity();
          return;
        }

        const productTitle =
          this.submitButton.dataset.productTitle ||
          document.querySelector('.product__title h1, .product__title')?.textContent?.trim() ||
          'Product';
        const variantTitle = this.submitButton.dataset.variantTitle || '';
        const variantId = this.variantIdInput?.value || '';
        notifyForm.querySelector('[name="contact[body]"]').value = [
          'Notify me request',
          `Product: ${productTitle}`,
          variantTitle ? `Variant: ${variantTitle}` : '',
          variantId ? `Variant ID: ${variantId}` : '',
          `Page: ${window.location.href}`,
        ]
          .filter(Boolean)
          .join('\n');

        const submit = notifyForm.querySelector('button[type="submit"]');
        submit.setAttribute('disabled', 'disabled');
        fetch('/contact', {
          method: 'POST',
          body: new FormData(notifyForm),
          headers: { Accept: 'text/html' },
        })
          .then((response) => {
            if (!response.ok) throw new Error('Notify request failed');
            message.textContent = "You're on the list.";
            message.hidden = false;
            notifyForm.reset();
          })
          .catch(() => {
            message.textContent = 'Something went wrong. Please try again.';
            message.hidden = false;
          })
          .finally(() => submit.removeAttribute('disabled'));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const paymentButton = this.querySelector('.zuke-payment-button-wrapper, .shopify-payment-button');
        const notifyPanel = this.querySelector('.zuke-notify-me-panel');

        if (text === 'Notify Me') {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('name');
          this.submitButton.setAttribute('type', 'button');
          this.submitButton.setAttribute('data-notify-me-button', '');
          this.submitButtonText.textContent = text;
          if (paymentButton) paymentButton.classList.add('hidden');
          return;
        }

        this.submitButton.removeAttribute('data-notify-me-button');
        this.submitButton.setAttribute('type', 'submit');
        this.submitButton.setAttribute('name', 'add');
        if (notifyPanel) notifyPanel.hidden = true;
        if (paymentButton) paymentButton.classList.remove('hidden');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
